import * as React from "react";
import {none, option, Option} from "ts-option";
import {FormGroupProps} from "../../../common/props/FormGroupProps";
import {PlaceholderProps} from "../../../common/props/PlaceholderProps";
import {InputActionMeta} from "react-select/lib/types";
import FormGroup from "../../../../hiberUiKit/src/molecules/FormGroup/FormGroup";
import {RemovableString} from "./StringMultiSelector";
import StringSortableMultiSelector from "./StringSortableMultiSelector";

interface State {
	/**
	 * Text displayed when create option and validation error
	 */
	promptText: string
	/**
	 * No result or non unique text
	 */
	noResultText: string
	/**
	 * Selector input value
	 */
	inputValue: string;
	/**
	 * Saved options
	 */
	savedFieldValues: string[]
}

type Props = FormGroupProps<string[]> & PlaceholderProps & PlaceholderProps ;

/**
 * Field option multi selector.
 */
export default class EnumFieldOptionsFormGroup extends React.PureComponent <Props, State> {
	constructor(props: Props) {
		super(props);
		this.onChange = this.onChange.bind(this);
		this.onInputChange = this.onInputChange.bind(this);
		this.onAdd = this.onAdd.bind(this);
		this.isUnique = this.isUnique.bind(this);
		this.promptTextCreator = this.promptTextCreator.bind(this);
		this.onInputKeyDown = this.onInputKeyDown.bind(this);
		this.getNewOptionData = this.getNewOptionData.bind(this);
		this.state = {
			promptText: "Create enum option:",
			noResultText: "",
			inputValue: "",
			savedFieldValues: props.field.getOptionalValue().getOrElse([])
		}
	}

	render() {
		return (
			<FormGroup label={this.props.label} error={this.props.field.firstError().getOrElse("")}>
				<StringSortableMultiSelector items={none}
											 value={this.getRemovableStringArray(this.props.field.getOptionalValue())}
											 inputValue={this.state.inputValue}
											 onInputChange={this.onInputChange}
											 onChanged={this.onChange}
											 onAddOption={this.onAdd}
											 placeholder={this.props.placeholder}
											 clearable={false}
											 noResultsText={this.state.noResultText}
											 promptTextCreator={this.promptTextCreator}
											 onInputKeyDown={this.onInputKeyDown}
											 getNewOptionData={this.getNewOptionData}
											 backspaceRemovesValue={false}/>
			</FormGroup>
		);
	}

	getRemovableStringArray(array: Option<string[]>): Option<RemovableString[]> {
		return array.map(arr => arr.map(
			(item, index) => {return {label: item, removable: this.state.savedFieldValues.indexOf(item) === -1, index}}))
	}

	/**
	 * Callback on changed string list.
	 * @param options
	 */
	onChange(options: Option<RemovableString[]>) {
		const updatedOptions = options.getOrElse([]);
		const value = updatedOptions.length ? option(updatedOptions.map(item => item.label)) : none;
		this.props.onChange(this.props.field.updateValue(value))
	}

	/**
	 * Selector input change callback.
	 * @param {string} inputValue
	 * @param {InputActionMeta} inputActionMeta
	 */
	onInputChange(inputValue: string, inputActionMeta: InputActionMeta) {
		if (inputActionMeta.action === 'input-change') {
			this.setState({inputValue});
		}
	}

	/**
	 * Callback on add new option.
	 * @param {string} newOption
	 */
	onAdd(newOption: string) {
		if (this.isUnique(newOption)) {
			if (newOption.trim()) {
				this.setState({inputValue: ""},
					() => this.props.onChange(this.props.field.updateValue(
						option(this.props.field.value.getOrElse([]).concat(newOption)))));
			}
		}
		else {
			this.setState({promptText: "Enum option already exists: "});
		}
	}

	/**
	 * Check if option is unique
	 * @returns {boolean}
	 */
	isUnique(newItem: string) {
		return this.props.field.getOptionalValue().getOrElse([]).indexOf(newItem) === -1;
	}

	/**
	 * Prompt text creator
	 * @param {string} filterText
	 * @returns {string}
	 */
	promptTextCreator(filterText: string): string {
		return `${this.state.promptText} ${filterText}`;
	}

	/**
	 * On input key down callback
	 */
	onInputKeyDown() {
		this.setState({promptText: "Create enum option:"});
	}

	/**
	 * Returns the data for the new option when it is created.
	 * @param {string} value
	 * @param {string} label
	 * @returns {string}
	 */
	getNewOptionData(value: string, label: string): RemovableString {
		return {label, removable: true};
	}
}